// Marka adı ve logo metinleri ancak `/settings` yanıtıyla bilinir; ilk boyamada
// henüz elde yoktur. Kuruluma özel bir varsayılan koda gömülemez (aynı yazılım
// farklı markalarla kurulur), boş bırakılınca da sayfa markasız açılır. Bu
// yüzden son bilinen marka localStorage'a yazılır ve açılışta oradan okunur —
// favicon (lib/favicon.js) ve tema (lib/theme.js) önbellekleriyle aynı yöntem.
// index.html içindeki küçük betik aynı anahtarı okuyup sekme başlığını yazar;
// anahtarı değiştirirseniz orayı da güncelleyin.
import { resolveApiAssetUrl } from './api';

export const BRAND_CACHE_KEY = 'app:brand-v1';

// Sekme başlığına ve logoya yazılacak kadar; uzun değer önbelleğe alınmaz.
const MAX_LENGTH = 80;

const clean = (value, limit = MAX_LENGTH) => String(value ?? '').trim().slice(0, limit);

const storageOf = (storage) => (
  storage !== undefined
    ? storage
    : (typeof window !== 'undefined' ? window.localStorage : null)
);

export const readCachedBrand = (storage) => {
  try {
    const raw = storageOf(storage)?.getItem(BRAND_CACHE_KEY);
    const data = raw ? JSON.parse(raw) : null;
    if (!data || typeof data !== 'object') return {};
    return {
      site_name: clean(data.site_name),
      logo_text_primary: clean(data.logo_text_primary),
      logo_text_secondary: clean(data.logo_text_secondary),
      logo_image_url: clean(data.logo_image_url, 300),
      loader_logo_url: clean(data.loader_logo_url, 300),
    };
  } catch {
    // Gizli/katı tarayıcı oturumlarında localStorage okunamayabilir.
    return {};
  }
};

// Ayarlar geldiğinde çağrılır. Marka panelde boşaltılmışsa önbellek de silinir,
// yoksa kaldırılmış bir ad sonraki açılışta geri gelirdi.
export const cacheBrand = (settings = {}, storage) => {
  const logo = clean(settings.logo_image_url, 300);
  // CMS › Logo › Loader: açılış ekranının kendi logosu (boşsa site logosu).
  const loader = clean(settings.loader_logo_url, 300);
  const brand = {
    site_name: clean(settings.site_name),
    logo_text_primary: clean(settings.logo_text_primary),
    logo_text_secondary: clean(settings.logo_text_secondary),
    // Açılış ekranı bunu doğrudan <img src> yapar; adres orada çözülemez.
    logo_image_url: logo ? clean(resolveApiAssetUrl(logo), 300) : '',
    loader_logo_url: loader ? clean(resolveApiAssetUrl(loader), 300) : '',
  };
  try {
    const target = storageOf(storage);
    if (!brand.site_name && !brand.logo_text_primary && !brand.logo_image_url) {
      target?.removeItem(BRAND_CACHE_KEY);
    } else target?.setItem(BRAND_CACHE_KEY, JSON.stringify(brand));
  } catch {
    // Depolama kapalıysa marka yine normal çalışır, yalnızca ilk boyama geç kalır.
  }
  return brand;
};

// Site adı: ayarlar geldiyse ondan, gelmediyse önbellekten. İkisi de yoksa boş
// döner — yanlış bir marka ("Site", "BHS") göstermektense hiç göstermemek yeğdir.
export const brandName = (settings = {}) => (
  clean(settings.site_name) || readCachedBrand().site_name || ''
);

// Logo metinleri aynı sırayla çözülür; ikinci metin ilkine bağlıdır, tek başına
// önbellekten alınırsa yarım bir logo çizilirdi.
export const brandLogoTexts = (settings = {}) => {
  const primary = clean(settings.logo_text_primary) || clean(settings.site_name);
  if (primary) {
    return { primary, secondary: clean(settings.logo_text_secondary) };
  }
  const cached = readCachedBrand();
  return {
    primary: cached.logo_text_primary || cached.site_name || '',
    secondary: (cached.logo_text_primary || cached.site_name)
      ? cached.logo_text_secondary || ''
      : '',
  };
};

export default brandName;
